import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { Store } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, takeUntil } from "rxjs/operators";
import { BehaviorSubject, Observable, combineLatest, Subject } from "rxjs";
import { AgGridAngular } from "ag-grid-angular";
import { ColDef, GridOptions, GridReadyEvent, CellClickedEvent, GridApi } from "ag-grid-community";
import { myCustomTheme } from "../../constants/grid-themes";
import { EditDeleteRendererComponent } from "@components/shared/edit-delete-renderer/edit-delete-renderer.component";
import { CreateProviderModalComponent, CreateProviderModalData } from "@components/modals/create-provider-modal/create-provider-modal.component";
import { ClinicianDetailsActions } from "@store/actions/clinician-details/clinician-details.actions";
import { selectIsLoading, selectProviders } from "@store/selectors/clinician-details/clinician-details.selectors";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { Provider } from "@models/provider.model";
import { UserProviderDto } from "@models/user.model";
import { GridExportService } from "@services/grid-export.service";
import { ProviderUploadModalComponent } from "@components/modals/provider-upload-modal/provider-upload-modal.component";

@Component({
  selector: "app-clinician-details",
  imports: [
    FormsModule,
    MatCardModule,
    CommonModule,
    MatButtonModule,
    AgGridAngular,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule
  ],
  templateUrl: "./clinician-details.component.html",
  styleUrl: "./clinician-details.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClinicianDetailsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly gridExportService = inject(GridExportService);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  private readonly orgId$ = this.store.select(selectOrgId);
  private readonly orgId = toSignal(this.orgId$);
  
  private readonly allProviders$ = this.store.select(selectProviders);
  readonly isLoading$ = this.store.select(selectIsLoading);

  private showInactiveSubject = new BehaviorSubject<boolean>(false);
  
  isLoading = false;
  
readonly dataSource$: Observable<Provider[]> = combineLatest([
  this.allProviders$,
  this.showInactiveSubject.asObservable()
]).pipe(
  map(([providers, showInactive]: [Provider[] | null, boolean]) => {
    if (!providers) return [];
    
    if (showInactive) {
      return providers;
    } else {
      const activeProviders = providers.filter((provider: Provider) => provider.active === 1);
      return activeProviders;
    }
  })
);



  readonly theme = myCustomTheme;
  private gridApi!: GridApi;
  
  showInactive = false;
  
  readonly gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    editType: "fullRow",
    onGridReady: this.onGridReady.bind(this),
  };

  readonly defaultColDef: ColDef = {
    flex: 1,
    filter: true,
    editable: false,
  };

  readonly frameWorkComponents = {
    editDeleteRendererComponent: EditDeleteRendererComponent,
  };

  colDefs: ColDef[] = [];

  ngOnInit(): void {
    this.store.dispatch(ClinicianDetailsActions.clinicianDetailsComponentLoaded());
    this.store.dispatch(ClinicianDetailsActions.getOrgProviders());
    this.store.dispatch(ClinicianDetailsActions.getProviderTaxonomies());
    this.setColDefs();
    
    this.isLoading$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(loading => {
      this.isLoading = loading;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onShowInactiveChange(): void {
  this.showInactiveSubject.next(this.showInactive);
}

  private setColDefs(): void {
    this.colDefs = [
      {
        field: "name",
        headerName: "Name",
        tooltipValueGetter: (params) => `${params.data.firstName} ${params.data.lastName}`,
        valueFormatter: (params) => `${params.data.firstName} ${params.data.lastName}`,
        valueGetter: (params) => `${params.data.firstName} ${params.data.lastName}`,
        sort: 'asc',
        flex: 2
      },
      {
        field: "npi",
        headerName: "NPI",
        tooltipField: "npi",
        flex: 1.5
      },
      {
        field: "providerUserEmailAddress",
        headerName: "Email",
        tooltipValueGetter: (params) => params.data.providerUserEmailAddress || '',
        flex: 2,
        cellRenderer: (params: any) => {
          const email = params.data.providerUserEmailAddress || '';
          if (params.data.providerUserId) {
            return `<span style="display:flex;align-items:center;gap:6px;height:100%;overflow:hidden">
              <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${email}</span>
              <span style="flex-shrink:0;background:#e3f2fd;color:#1565c0;border-radius:10px;padding:1px 8px;font-size:11px;font-weight:600;line-height:1.6">User</span>
            </span>`;
          }
          return `<span style="display:flex;align-items:center;height:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${email}</span>`;
        }
      },
            {
        field: "phone",
        headerName: "Phone Number",
        tooltipField: "phone",
        flex: 2
      },
      {
        field: "providerTaxonomyDescription",
        headerName: "Specialty",
        tooltipField: "providerTaxonomyDescription",
        flex: 1.5,
        valueFormatter: (params) => {
          const fullValue = params.data.providerTaxonomyDescription;
          if (!fullValue) return '';
        
          const lastSlashIndex = fullValue.lastIndexOf('/');
          return lastSlashIndex !== -1 ? fullValue.substring(lastSlashIndex + 1) : fullValue;
        }
      },
      {
        field: "dateRange",
        headerName: "Date Range",
        flex: 2,
        valueGetter: (params) => this.formatDateRange(params.data.effectiveDate, params.data.expirationDate),
        tooltipValueGetter: (params) => this.formatDateRange(params.data.effectiveDate, params.data.expirationDate),
      },
      {
        field: "actions",
        headerName: "Actions",
        filter: false,
        editable: false,
        suppressNavigable: true,
        cellRenderer: "editDeleteRendererComponent",
        cellRendererParams: (params: any) => ({
          showEdit: true,
          showCreate: true,
          showDelete: true,
          
          disableDelete: params.data.active === 0,
          disableCreate: !!params.data.providerUserId,
          disableEdit: false,
          
          createLabel: 'Create User',
          deleteLabel: 'Deactivate Provider'
        }),
        flex: 1.5
      },
    ];
  }

openCreateProviderModal(event?: Event): void {

  if (event?.target instanceof HTMLElement) {
    event.target.blur();
  }
  
  const activeElement = document.activeElement as HTMLElement;
  if (activeElement && activeElement.id === 'createProviderButton') {
    activeElement.blur();
  }
  
  const dialogRef = this.dialog.open(CreateProviderModalComponent, {
    width: '800px',
    maxWidth: '90vw',
    disableClose: false,
    autoFocus: true,       
    restoreFocus: true      
  });

  dialogRef.afterClosed().subscribe((result: CreateProviderModalData | undefined) => {
    if (result) {
      this.store.dispatch(ClinicianDetailsActions.createProvider({
        providerData: {
          ...result,
          organizationID: this.getOrganizationId(),
        }
      }));
    }
  });
}

openUploadProvidersModal(): void {
  const dialogRef = this.dialog.open(ProviderUploadModalComponent, {
    panelClass: 'provider-upload-modal',
    disableClose: false,
    autoFocus: false,
    data: { organizationId: this.getOrganizationId() }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      this.store.dispatch(ClinicianDetailsActions.getOrgProviders());
    }
  });
}



  onCellClicked(params: CellClickedEvent): void {
    if (params.column?.getColId() === "actions" && params.event?.target) {
      const target = params.event.target as HTMLElement;
      const action = target.dataset['action'];
      
      if (action === "delete") {
        this.handleDeleteAction(params.data);
      } else if (action === "create") {
        this.handleCreateUserAction(params.data);
      } else if (action === "edit") {
        this.handleEditAction(params.data);
      }
    }
  }

  exportCsv(): void {
    if (this.gridApi) {
      this.gridExportService.exportToCsv(this.gridApi, {
        fileName: 'clinician_data_export',
        appendDateToFileName: true,
        columnKeys: ['name', 'npi', 'providerUserEmailAddress', 'providerTaxonomyDescription', 'dateRange']
      });
    }
  }

private handleDeleteAction(provider: Provider): void {
  if (provider.active === 1) {
    this.store.dispatch(
      ClinicianDetailsActions.deactivateProvider({
        name: `${provider.firstName} ${provider.lastName}`,
        organizationProviderId: provider.id,
      })
    );
  }
}

private handleCreateUserAction(provider: Provider): void {
  if (!provider.providerUserId) {
    const userData = this.convertProviderToDto(provider);
    
    this.store.dispatch(
      ClinicianDetailsActions.createUserFromProviderPressed({ 
        organizationProviderId: provider.id,
        providerData: userData
      })
    );
  }
}
  private handleEditAction(provider: Provider): void {
    const dialogRef = this.dialog.open(CreateProviderModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      disableClose: false,
      autoFocus: true,
      data: {
        provider: provider,
        isEdit: true
      }
    });

    dialogRef.afterClosed().subscribe((result: CreateProviderModalData | undefined) => {
      if (result) {
        this.store.dispatch(ClinicianDetailsActions.updateProvider({
          providerId: provider.id,
          providerData: {
            ...result,
            organizationID: this.getOrganizationId(),
          }
        }));
      }
    });
  }

  private formatDateRange(startDate?: string | null, endDate?: string | null): string {
    if (!startDate && !endDate) return '';
    const fmt = (d: string) => {
      const parts = d.split('-');
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    };
    if (startDate && endDate) return `${fmt(startDate)} – ${fmt(endDate)}`;
    if (startDate) return `${fmt(startDate)} – Present`;
    return `– ${fmt(endDate!)}`;
  }

  private getOrganizationId(): string {
    const orgId = this.orgId();
    return orgId ? String(orgId) : '0';
  }

private convertProviderToDto(provider: Provider, userFormData?: any): UserProviderDto {
    const orgId = this.getOrganizationId();
    return {
      id: "",
      firstName: provider.firstName,
      lastName: provider.lastName,
      userName: userFormData?.userName || provider.providerUserEmailAddress || "",
      emailAddress: provider.providerUserEmailAddress || "",
      b2cID: "",
      organizationIds: [Number(orgId)],
      isSuperAdmin: 0,
      providerId: provider.id,
      active: 1,
      roleId: userFormData?.roleId || "",
      lastLogin: undefined
    };
  }
}